import {isEPCustomLike} from '../../rendering/render.def';

export const CAF_DOC_MIN = 5;
export const CAF_DOC_MAX = 24;
export const CAF_DOC_STEP = 0.125;

export const snapToEighth = (n: number): number => Math.round(n / CAF_DOC_STEP) * CAF_DOC_STEP;

export const isValidCafDocDimension = (n: number): boolean => Number.isFinite(n) && n >= CAF_DOC_MIN && n <= CAF_DOC_MAX && Number.isInteger(n / CAF_DOC_STEP);

/** Every selectable desktop CAF dimension: 5" to 24" inclusive in 1/8" steps (153 values). */
export const cafDocSizeOptions = (): number[] => {
    const options: number[] = [];
    for (let eighths = CAF_DOC_MIN / CAF_DOC_STEP; eighths <= CAF_DOC_MAX / CAF_DOC_STEP; eighths++) {
        options.push(eighths * CAF_DOC_STEP);
    }
    return options;
};

/** Dropdown label for a dimension: 8.5 -> 8.5", 11 -> 11", 8.125 -> 8.125". */
export const formatCafDocSize = (n: number): string => `${n}"`;

export type CafDocSizeFields = {
    doc1_w: number;
    doc1_h: number;
    doc2_w: number;
    doc2_h: number;
    print1_w: number;
    print1_h: number;
    print2_w: number;
    print2_h: number;
};

export type CafDocSizeValidationInput = CafDocSizeFields & {
    docType: string;
    dyfFrameStyleId: number;
    showBothDocSizes: boolean;
    epMethodId: number;
    hasEngravedPlate: boolean;
    epWidth: number;
    /** Optional: inject EP clip check so tests need not load the Quill editor module. */
    isEpContentClippedForDoc?: (docWidth: number, docHeight: number) => boolean;
};

export type CafDocSizeValidationResult = {
    error1: string;
    error2: string;
    error3: string;
    error4: string;
    epOverflowing: boolean;
    /** True when any dimension/print/plate error is set (blocks submit and skips applying sizes). */
    hasFieldErrors: boolean;
};

/**
 * Pure CAF sizing validation used by CAFDocSize before add_to_cart / approval.
 * Preserves the existing error-field assignment order and messages.
 */
export const validateCafDocSizes = (input: CafDocSizeValidationInput): CafDocSizeValidationResult => {
    const docType = input.docType;
    const docTypeLower = docType.toLowerCase();

    let error1 = '';
    let error2 = '';
    let error3 = '';
    let error4 = '';

    if (input.dyfFrameStyleId === 2 && input.showBothDocSizes) {
        if (!input.doc2_w) {
            return {
                error1: '',
                error2: '',
                error3: `Invalid bottom ${docTypeLower} width`,
                error4: '',
                epOverflowing: false,
                hasFieldErrors: true,
            };
        }
        if (!input.doc2_h) {
            return {
                error1: '',
                error2: '',
                error3: `Invalid bottom ${docTypeLower} height`,
                error4: '',
                epOverflowing: false,
                hasFieldErrors: true,
            };
        }
        if (input.print2_w && input.print2_w > 0 && input.print2_w < 1) {
            error2 = `Bottom printed width is too low. Must be greater than 1.`;
        }
        if (input.print2_h && input.print2_h > 0 && input.print2_h < 1) {
            error2 = `Bottom printed height is too low. Must be greater than 1.`;
        }
        if (input.print2_w && input.print2_w > 0 && input.print2_w > 30) {
            error4 = `Bottom printed width is too high. Must be less than 30 inches.`;
        }
        if (input.print2_h && input.print2_h > 0 && input.print2_h > 24) {
            error4 = `Bottom printed height is too high. less than 24 inches.`;
        }
        if (input.print2_w && input.print2_w > 0 && input.print2_w > input.doc2_w) {
            error2 = `Bottom printed width is too high. Must be less than bottom ${docTypeLower} width.`;
        }
        if (input.print2_h && input.print2_h > 0 && input.print2_h > input.doc2_h) {
            error2 = `Bottom printed height is too high. Must be less than bottom ${docTypeLower} height.`;
        }

        if (input.doc2_w < 1) {
            error3 = `Bottom ${docType} width is too low. Must be greater than 1.`;
        }
        if (input.doc2_h < 1) {
            error3 = `Bottom ${docType} height is too low. Must be greater than 1.`;
        }
        if (input.doc2_w > 30) {
            error3 = `Bottom ${docType} width is too high. Must be less than 30 inches.`;
        }
        if (input.doc2_h > 24) {
            error3 = `Bottom ${docType} height is too high. Must be less than 24 inches.`;
        }
    }

    if (!input.doc1_w) {
        return {
            error1: `Invalid ${docTypeLower} width`,
            error2,
            error3,
            error4,
            epOverflowing: false,
            hasFieldErrors: true,
        };
    }
    if (!input.doc1_h) {
        return {
            error1: `Invalid ${docTypeLower} height`,
            error2,
            error3,
            error4,
            epOverflowing: false,
            hasFieldErrors: true,
        };
    }
    if (input.doc1_w < 1) {
        error1 = `${docType} width is too low. Must be greater than 1.`;
    }
    if (input.doc1_h < 1) {
        error1 = `${docType} height is too low. Must be greater than 1.`;
    }
    if (input.doc1_w > 30) {
        error1 = `${docType} width is too high. Must be less than 30 inches.`;
    }
    if (input.doc1_h > 24) {
        error1 = `${docType} height is too high. Must be less than 24 inches.`;
    }

    if (input.print1_w && input.print1_w > 0 && input.print1_w < 1) {
        error2 = `Printed width is too low. Must be greater than 1.`;
    }
    if (input.print1_h && input.print1_h > 0 && input.print1_h < 1) {
        error2 = `Printed height is too low. Must be greater than 1.`;
    }
    if (input.print1_w && input.print1_w > 0 && input.print1_w > 30) {
        error2 = `Printed width is too high. Must be less than 30 inches.`;
    }
    if (input.print1_h && input.print1_h > 0 && input.print1_h > 24) {
        error2 = `Printed height is too high. Must be less than 24 inches.`;
    }

    if (input.print1_w && input.print1_w > 0 && input.print1_w > input.doc1_w) {
        error2 = `Printed width is too high. Must be less than ${docTypeLower} width.`;
    }
    if (input.print1_h && input.print1_h > 0 && input.print1_h > input.doc1_h) {
        error2 = `Printed height is too high. Must be less than ${docTypeLower} height.`;
    }

    if (isEPCustomLike(input.epMethodId) && input.epWidth > input.doc1_w) {
        error1 = `Personalized plate width (${input.epWidth}") exceeds ${docTypeLower} width (${input.doc1_w}"). Please select a smaller plate size.`;
    }

    let epOverflowing = false;
    if (isEPCustomLike(input.epMethodId) && input.hasEngravedPlate && input.doc1_w > 0 && input.doc1_h > 0) {
        epOverflowing = input.isEpContentClippedForDoc ? input.isEpContentClippedForDoc(input.doc1_w, input.doc1_h) : false;
    }

    const hasFieldErrors = error1 !== '' || error2 !== '' || error3 !== '' || error4 !== '';
    return {error1, error2, error3, error4, epOverflowing, hasFieldErrors};
};

/** Post-validation submit branch for CAF ATC (non-renderFrame path). */
export type CafDocSizeSubmitAction = 'blocked_overflow' | 'busy' | 'approval' | 'add_to_cart';

export const getCafDocSizeSubmitAction = (opts: {epOverflowing: boolean; atcBusy: boolean; needsApproval: boolean}): CafDocSizeSubmitAction => {
    if (opts.epOverflowing) {
        return 'blocked_overflow';
    }
    if (opts.atcBusy) {
        return 'busy';
    }
    if (opts.needsApproval) {
        return 'approval';
    }
    return 'add_to_cart';
};
